import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input } from "../components/UI";
import { toast } from "sonner";
import {
  Users, Loader2, UserPlus, Trash2, ShieldCheck, X, Copy, ChevronDown, Building2, Mail
} from "lucide-react";
import { useChecklistReturn, ChecklistBackLink } from "../lib/checklistReturn";

const MEMBER_ROLES = [
  { key: "candidate", label: "Candidate" },
  { key: "campaign_admin", label: "Campaign Admin" },
  { key: "campaign_manager", label: "Campaign Manager" },
  { key: "treasurer", label: "Treasurer" },
  { key: "compliance_officer", label: "Compliance" },
  { key: "communications", label: "Communications" },
  { key: "field", label: "Field / Volunteers" },
  { key: "digital", label: "Digital" },
  { key: "finance", label: "Finance" },
  { key: "staff", label: "Staff (view-only)" },
];

export default function AgencyTeam() {
  const { fromChecklist, returnToChecklist } = useChecklistReturn();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/political-os/agency/team");
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, "Could not load agency team")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateRoles = async (entity_id, user_id, roles) => {
    try {
      await api.put("/political-os/agency/team/roles", { entity_id, user_id, roles });
      toast.success(roles.length ? "Roles updated" : "Member removed from that entity");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading || !data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  const entities = data.entities || [];
  const team = data.team || [];
  const invites = data.pending_invitations || [];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="agency-team-page">
      <ChecklistBackLink show={fromChecklist} />
      <PageHeader
        eyebrow="Agency"
        title="Team & Access"
        subtitle="One place to see who has access to every entity in your portfolio, and to bulk-invite new consultants or staff across many campaigns at once."
        action={
          <Btn onClick={() => setInviteOpen(true)} data-testid="agency-invite-btn" disabled={!entities.length}>
            <UserPlus className="w-4 h-4" /> Bulk invite
          </Btn>
        }
      />

      {!entities.length && (
        <Card><div className="text-center py-8 text-sm text-slate-400" data-testid="agency-team-empty">You don&apos;t manage any entities yet — add a client from the Portfolio to invite team members.</div></Card>
      )}

      {/* Team matrix */}
      {team.length > 0 && (
        <Card padded={false} data-testid="agency-team-matrix">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Users className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-lg font-bold text-white">Team members ({team.length})</h3>
            <div className="text-xs text-slate-500 ml-2">Across {entities.length} entit{entities.length === 1 ? "y" : "ies"}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3">Person</th>
                  <th className="px-5 py-3">Entities</th>
                  <th className="px-5 py-3 text-right">Roles</th>
                </tr>
              </thead>
              <tbody>
                {team.map((m) => (
                  <tr key={m.user_id} className="border-b border-white/5 align-top" data-testid={`agency-team-row-${m.user_id}`}>
                    <td className="px-5 py-3">
                      <div className="text-slate-200 font-medium">{m.name || m.email || "Unknown"}</div>
                      {m.email && <div className="text-[10px] text-slate-500">{m.email}</div>}
                      {m.last_login && <div className="text-[10px] text-slate-600 mt-0.5">Last seen {(m.last_login || "").slice(0, 10)}</div>}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-200 px-2 py-0.5 font-semibold">
                        <Building2 className="w-3 h-3" /> {m.total_entities}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="space-y-1.5" data-testid={`agency-team-entities-${m.user_id}`}>
                        {m.entities.map((e) => (
                          <RoleEditor
                            key={e.entity_id}
                            member={m}
                            entity={e}
                            onSave={(roles) => updateRoles(e.entity_id, m.user_id, roles)}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pending invitations */}
      {invites.length > 0 && (
        <Card padded={false} data-testid="agency-pending-invites">
          <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-lg font-bold text-white">Pending invitations ({invites.length})</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Entity</th>
                <th className="px-5 py-3">Roles</th>
                <th className="px-5 py-3">Expires</th>
                <th className="px-5 py-3">Invited by</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv, i) => (
                <tr key={inv.id || i} className="border-b border-white/5" data-testid={`agency-invite-${inv.id}`}>
                  <td className="px-5 py-3 text-slate-200">{inv.email}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs">{inv.entity_name}</td>
                  <td className="px-5 py-3 text-xs text-cyan-300">{(inv.roles || []).join(", ").replace(/_/g, " ")}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{(inv.expires_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{inv.invited_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {inviteOpen && (
        <BulkInviteModal
          entities={entities}
          onClose={() => setInviteOpen(false)}
          onDone={() => { setInviteOpen(false); if (returnToChecklist()) return; load(); }}
        />
      )}
    </div>
  );
}

function RoleEditor({ member, entity, onSave }) {
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState(entity.roles || []);

  const toggle = (r) => setRoles(rs => rs.includes(r) ? rs.filter(x => x !== r) : [...rs, r]);
  const save = () => { setOpen(false); onSave(roles); };
  const removeEntity = () => {
    if (!window.confirm(`Remove ${member.name || member.email} from ${entity.entity_name}?`)) return;
    setRoles([]); onSave([]);
  };

  return (
    <div className="inline-flex items-center gap-2 flex-wrap justify-end" data-testid={`role-editor-${entity.entity_id}-${member.user_id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid={`role-editor-toggle-${entity.entity_id}`}
        className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-slate-900/60 px-2.5 py-1 text-xs text-slate-300 hover:border-cyan-400/40"
      >
        <span className="text-slate-500">{entity.entity_name}:</span>
        <span className="text-cyan-300">{(entity.roles || []).slice(0, 2).map(r => r.replace(/_/g, " ")).join(", ") || "—"}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="w-full bg-slate-900/80 border border-white/10 rounded-md p-3 mt-1 text-left" data-testid={`role-editor-panel-${entity.entity_id}`}>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {MEMBER_ROLES.map(r => (
              <button
                key={r.key}
                onClick={() => toggle(r.key)}
                data-testid={`role-chip-${entity.entity_id}-${r.key}`}
                className={`text-[10px] px-2 py-1 rounded-full border ${roles.includes(r.key)
                  ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200"
                  : "border-white/15 text-slate-400 hover:border-cyan-400/30"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center gap-2 mt-3">
            <button onClick={removeEntity} className="text-xs text-rose-300 hover:underline inline-flex items-center gap-1" data-testid={`role-editor-remove-${entity.entity_id}`}>
              <Trash2 className="w-3 h-3" /> Remove from entity
            </button>
            <div className="flex gap-2">
              <button onClick={() => setOpen(false)} className="text-xs text-slate-400 hover:text-white">Cancel</button>
              <button onClick={save} data-testid={`role-editor-save-${entity.entity_id}`} className="text-xs px-3 py-1 rounded-md bg-cyan-400/20 text-cyan-200 border border-cyan-400/40 hover:bg-cyan-400/30">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BulkInviteModal({ entities, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [assignments, setAssignments] = useState(entities.map(e => ({ entity_id: e.id, name: e.name, roles: [] })));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const toggleRole = (eid, role) => {
    setAssignments(rows => rows.map(r =>
      r.entity_id === eid
        ? { ...r, roles: r.roles.includes(role) ? r.roles.filter(x => x !== role) : [...r.roles, role] }
        : r
    ));
  };
  const applyAllStaff = () => {
    setAssignments(rows => rows.map(r => ({ ...r, roles: ["staff"] })));
  };
  const applyAllCampaignManager = () => {
    setAssignments(rows => rows.map(r => ({ ...r, roles: ["campaign_manager"] })));
  };

  const submit = async () => {
    const picked = assignments.filter(r => r.roles.length > 0);
    if (!email.includes("@")) return toast.error("Valid email required");
    if (picked.length === 0) return toast.error("Assign at least one role in one entity");
    setSubmitting(true);
    try {
      const { data } = await api.post("/political-os/agency/invitations", {
        email, assignments: picked.map(r => ({ entity_id: r.entity_id, roles: r.roles }))
      });
      setResult(data);
      toast.success(`Invited to ${data.accepted.length} entity${data.accepted.length === 1 ? "" : "ies"}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSubmitting(false); }
  };

  const copyLink = () => {
    if (!result?.accept_url) return;
    navigator.clipboard?.writeText(result.accept_url).then(() => toast.success("Accept link copied"));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="agency-invite-modal">
      <div className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Agency</div>
            <h3 className="font-display text-lg font-bold text-white">Bulk-invite one person to multiple entities</h3>
          </div>
          <button onClick={onClose} data-testid="agency-invite-close"><X className="w-5 h-5 text-slate-400 hover:text-white" /></button>
        </div>

        {!result ? (
          <div className="p-6 space-y-4 overflow-y-auto">
            <Input label="Email" value={email} onChange={e => setEmail(e.target.value)}
                   placeholder="teammate@agency.com" data-testid="agency-invite-email-input" />

            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Roles per entity</div>
              <div className="flex gap-2 text-[10px]">
                <button onClick={applyAllStaff} data-testid="agency-invite-apply-staff" className="px-2 py-1 rounded-md border border-white/10 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300">Everyone as Staff</button>
                <button onClick={applyAllCampaignManager} data-testid="agency-invite-apply-cm" className="px-2 py-1 rounded-md border border-white/10 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300">Everyone as Campaign Mgr</button>
              </div>
            </div>

            <div className="space-y-2 max-h-[40vh] overflow-y-auto" data-testid="agency-invite-assignments">
              {assignments.map(a => (
                <div key={a.entity_id} className="rounded-lg border border-white/10 bg-slate-900/40 p-3" data-testid={`agency-invite-row-${a.entity_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-4 h-4 text-cyan-300" />
                    <div className="text-sm text-white font-medium">{a.name}</div>
                    {a.roles.length > 0 && (
                      <span className="text-[10px] text-cyan-300 ml-auto">{a.roles.length} role{a.roles.length > 1 ? "s" : ""}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {MEMBER_ROLES.map(r => (
                      <button
                        key={r.key}
                        onClick={() => toggleRole(a.entity_id, r.key)}
                        data-testid={`agency-invite-role-${a.entity_id}-${r.key}`}
                        className={`text-[10px] px-2 py-1 rounded-full border ${a.roles.includes(r.key)
                          ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200"
                          : "border-white/15 text-slate-400 hover:border-cyan-400/30"}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
              <Btn onClick={submit} disabled={submitting} data-testid="agency-invite-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Send invitation
              </Btn>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4 overflow-y-auto" data-testid="agency-invite-result">
            <div className="text-emerald-300 font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Invitation sent</div>
            <div className="text-sm text-slate-300">{email} was invited to {result.accepted.length} entit{result.accepted.length === 1 ? "y" : "ies"}.</div>
            {(result.skipped || []).length > 0 && (
              <div className="text-xs text-amber-300">{result.skipped.length} entit{result.skipped.length === 1 ? "y" : "ies"} skipped (already invited or member)</div>
            )}
            <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-xs space-y-1">
              {result.accepted.map(a => (
                <div key={a.entity_id} className="flex items-center justify-between gap-3">
                  <span className="text-slate-200">{a.entity_name}</span>
                  <span className="text-cyan-300">{(a.roles || []).join(", ").replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-xs">
              <div className="text-slate-500 mb-1">Accept link</div>
              <div className="flex items-center gap-2">
                <code className="text-cyan-300 break-all flex-1">{result.accept_url}</code>
                <button onClick={copyLink} data-testid="agency-invite-copy-link" className="p-1.5 rounded-md border border-white/10 text-slate-400 hover:text-cyan-300"><Copy className="w-3 h-3" /></button>
              </div>
              <div className="text-[10px] text-slate-600 mt-2">Expires {(result.expires_at || "").slice(0, 10)}</div>
            </div>
            <div className="flex justify-end pt-2">
              <Btn onClick={onDone} data-testid="agency-invite-done-btn">Done</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
